import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { HTMLNodeTreeWalker } from "../graphs/HTMLNodeTreeWalker";
import { ExtendedRegex } from "../text/ExtendedRegex";
import { Lexer } from "../text/lexer/Lexer";
import { LexerMatcher } from "../text/lexer/LexerMatcher";
import { SVGTextLexer, SVGTextLexerType } from "./SVGTextLexer";
import { FontInfo } from "../fonts/FontInfo";
import { FontDescription } from "../fonts/FontDescription";
import { FontLibrary } from "../fonts/FontLibrary";
import { SVGEditor } from "./SVGEditor";
import { SVGTextFragment } from "./SVGTextFragment";
import { ElementType } from "../dom-tools/ElementType";
import { HTMLEditor } from "../dom-tools/HTMLEditor";


export class SVGTextSetter
{
  private _textElement:SVGTextElement;

  private _lineWidth:number = 0; 
  private _lineHeight:number = 0;

  private _fragments:SVGTextFragment[] = [];
  private _currentX:number = 0;
  private _offsetX:number = 0;
  private _currentLineIndex:number = 0;
  private _currentLineOffset:number = 0;
  private _currentLine:SVGTSpanElement = null;
  private _lineIsEmpty:boolean = true;

  private _fontDescription = new FontDescription();
  private _fontSize = 12;

  static readonly customLineWidth = new ElementAttribute( "custom-line-width" );

  setText( t:SVGTextElement, container:Element )
  {
    this._textElement = t;
    let style = t.style;

    this._offsetX = SVGEditor.x.asNumberFrom( t, 0 );
    this._currentLineOffset = SVGEditor.y.asNumberFrom( t, 0 );
    
    this._fontDescription.fontName = style.fontFamily;
    this._fontDescription.italic   = style.fontStyle === "italic";
    this._fontDescription.weight   = parseFloat( style.fontWeight ) || 400;

    this._fontSize = parseFloat( style.fontSize ) || 12;         

    if ( SVGTextSetter.customLineWidth.in( t ) )
    {
      this._lineWidth =  SVGTextSetter.customLineWidth.asNumberFrom( t );
    }
    else
    {
      this._lineWidth =  parseFloat( style.inlineSize ) || 10000;
    }

    console.log( "SETTING CUSTOM LINE WIDTH:", this._lineWidth, t );
    
    this._lineHeight = this._fontSize * ( parseFloat( style.lineHeight ) || 1.5 ) ;
    
    this._grabTextFragments( container );
    this._layoutText();
  }

  private _grabTextFragments( container:Element )
  {
    let walker = new HTMLNodeTreeWalker();

    let it:Node = container;
    let end = walker.iterationEndOf( it );   
    
    while ( it && it !== end )
    {

      let n = it;

      if ( "br" === n.nodeName.toLowerCase() )
      {
        let brFragment = new SVGTextFragment();
        brFragment.lineBreak = true;
        this._fragments.push( brFragment );         
      }
      else if ( Node.TEXT_NODE === n.nodeType )
      {

        let textFragment = new SVGTextFragment();
        textFragment.text = n.textContent;
        textFragment.href = this._getHref( n, container );

        console.log( textFragment.text, textFragment.href );
        
        let fontDescription = this._fontDescription;
        let fontInfo = FontLibrary.getFontInfo( fontDescription );
        let fontSize = this._fontSize;

        textFragment.computeTextAtoms( fontInfo, fontSize );
        this._fragments.push( textFragment );

      }
      
      it = walker.nextNode( it );
    }


  }

  private _lines:SVGTSpanElement[] = [];

  private _layoutText()
  {
    this._lines = [];
    this._createCurrentLine();
    this._fragments.forEach( f => this._addFragment( f )  );

    this._mergeAnchors();
  }

  private _mergeAnchors()
  {
    console.log( "MERGING ANCHORS ON:", this._lines.length, "LINES");
    this._lines.forEach( l => this._mergeAnchorOnLine( l ) );
  }

  private _getMergeRanges( line:SVGTSpanElement )
  {
    var start = -1;
    var href = null;

    var mergeRanges:number[][] = [];

    for ( let i = 0; i < line.children.length; i++ )
    {
      var element = line.children[ i ];

      if ( ElementAttribute.href.in( element ) )
      {
        var elementHref = ElementAttribute.href.from( element );
        var createNew = start == -1 || href !== elementHref;

        if ( ! createNew )
        {
          continue;          
        }

        if ( start !== -1 )
        {
          mergeRanges.push( [ start, i - 1 ] );
        }

        start = i;
        href = elementHref;
        
      }
      else
      {
        if ( start !== -1 )
        {
          mergeRanges.push( [ start, i - 1 ] );
          start = -1;
          href = null;
        }
      }

      
    }

    if ( start !== -1 )
    {
      mergeRanges.push( [ start, line.children.length - 1 ] );
    }

    return mergeRanges;
  }

  private _mergeAnchorOnLine( line:SVGTSpanElement )
  {
    let mergeRanges = this._getMergeRanges( line );
    let mergeInfos = mergeRanges.map( mr => `start:${mr[0]} end:${mr[1]}`);
    console.log( "MERGE RANGES: [", mergeInfos.join(", "), line.children.length, line.textContent );


    for ( let i = 0; i < mergeRanges.length; i++ )
    {
      var index = ( mergeRanges.length - 1 ) - i;      
      
      var rangeStart = mergeRanges[ index ][ 0 ];
      var rangeEnd   = mergeRanges[ index ][ 1 ];

      HTMLEditor.mergeAnchors( line, rangeStart, rangeEnd );
    }
  }


  private _createCurrentLine()
  {
    this._currentLine = SVGEditor.tspan.create( undefined, this._textElement.ownerDocument );
    this._textElement.appendChild( this._currentLine );
    this._lineIsEmpty = true;

    this._lines.push( this._currentLine );
    
    SVGEditor.x.to( this._currentLine, this._offsetX );
    SVGEditor.y.to( this._currentLine, this._currentLineOffset );
  }

  private _newLine()
  {
    this._currentX = 0;
    this._currentLineIndex ++;
    this._currentLineOffset += this._lineHeight;

    this._createCurrentLine();
  }

  private _addText( text:string, href:string )
  {
    let doc = this._textElement.ownerDocument;
    let tspan = SVGEditor.tspan.create( undefined, doc );
    tspan.appendChild( doc.createTextNode( text ) );

    this._lineIsEmpty = false;

    if ( href !== null )
    {
      let anchor = ElementType.anchor.create( undefined, doc );
      ElementAttribute.href.to( anchor, href );

      anchor.appendChild( tspan );
      this._currentLine.appendChild( anchor );
    }
    else
    {
      this._currentLine.appendChild( tspan );
    }
    
  }

  private _addFragment( fragment:SVGTextFragment )
  {
    if ( fragment.lineBreak )
    {
      this._newLine();
      return;
    }

    let atoms = fragment.atoms;

    for ( let i = 0; i < atoms.length; i++ )
    {
      let atom = atoms[ i ];
      let isSpace =  SVGTextLexer.SPACE == atom.type;

      if ( isSpace  )
      {
        if ( this._lineIsEmpty )
        {
          continue;
        }        
        
        this._addText( atom.text, fragment.href );
        this._currentX += atom.width;

        continue;
      }

      let insertWidth = this._currentX + atom.width;
      let tooBig = insertWidth > this._lineWidth;

      console.log( "LINE WIDTH:", "x:", this._currentX, { atom, insertWidth, tooBig } );

      if ( ! this._lineIsEmpty && tooBig )
      {
        this._newLine();
      }

      this._addText( atom.text, fragment.href );
      this._currentX += atom.width;
    }
    
  }

  private _getHref( node:Node, parent:Element ):string
  {
    let it = node.parentElement;

    while ( it !== null && it !== parent )
    {
      if ( ElementType.anchor.is( it ) && ElementAttribute.href.in( it ) )
      {
        return ElementAttribute.href.from( it );
      }

      it = it.parentElement;
    }

    return null;
  }

}